import {
  DocumentAnalysis,
  DocumentAnalysisStatus,
} from '@admiin-com/ds-graphql';
import { get, set, cloneDeep } from 'lodash';

/**
 * Field mappings for different scanning types
 * Each scanning type updates only specific fields to prevent data loss
 */
export const SCANNING_FIELD_MAPPINGS = {
  expense: {
    statusField: 'expenseStatus',
    fields: [
      'expenseStatus',
      'matchedContactId',
      'matchedContact',
      'newContact',
      'task.reference',
      'task.dueAt',
      'task.issuedAt',
      'task.gstInclusive',
      'task.notes',
      'task.amount',
      'task.gstAmount',
      'task.taxAmount',
      'task.lineItems',
      'task.numberOfPayments',
      'task.paymentFrequency',
      'task.shippingAmount',
      'task.partnerId',
      'task.managerId',
      'task.documentType',
      'task.settlementStatus',
      'task.period',
    ],
  },
  query: {
    statusField: 'queryStatus',
    fields: [
      'queryStatus',
      'matchedEntityId',
      'matchedEntity',
      'matchedBiller',
      'bpay',
      'bpay.billerCode',
      'bpay.referenceNumber',
      'bank',
      'bank.accountNumber',
      'bank.routingNumber',
    ],
  },
};

/**
 * Common fields that are always updated regardless of scanning type
 */
export const COMMON_FIELDS = [
  'id',
  'entityId',
  'fileKey',
  'documents',
  'status',
  'createdAt',
  'updatedAt',
];

/**
 * Identifies the type of scanning based on status field changes
 */
export const identifyScanningType = (
  existing: DocumentAnalysis | undefined,
  incoming: DocumentAnalysis
): 'expense' | 'query' | 'unknown' => {
  // Check if expense scanning just completed
  if (
    incoming.expenseStatus === DocumentAnalysisStatus.SCANNED &&
    existing?.expenseStatus !== DocumentAnalysisStatus.SCANNED
  ) {
    return 'expense';
  }

  // Check if query (payment) scanning just completed
  if (
    incoming.queryStatus === DocumentAnalysisStatus.SCANNED &&
    existing?.queryStatus !== DocumentAnalysisStatus.SCANNED
  ) {
    return 'query';
  }

  // If neither status changed to SCANNED, we can't determine the type
  return 'unknown';
};

/**
 * Smart merge function that only updates fields relevant to the scanning type
 * This prevents expense scanning from overwriting payment data and vice versa
 *
 * @param existing - The existing document analysis data (if any)
 * @param incoming - The new document analysis data from the subscription
 * @returns Merged document analysis preserving data from both scanning types
 */
export const smartMergeDocumentAnalysis = (
  existing: DocumentAnalysis | undefined,
  incoming: DocumentAnalysis
): DocumentAnalysis => {
  // If no existing data, just return the incoming data
  if (!existing) {
    return incoming;
  }

  // Identify which type of scanning produced this update
  const scanningType = identifyScanningType(existing, incoming);

  // If we can't identify the scanning type, fallback to full replacement
  // This should rarely happen in practice
  if (scanningType === 'unknown') {
    console.warn('Unable to identify scanning type, using full replacement');
    return incoming;
  }

  // Get the field mapping for this scanning type
  const fieldMapping = SCANNING_FIELD_MAPPINGS[scanningType];

  // Start with a deep clone of existing data to preserve all fields
  const merged = cloneDeep(existing);

  // Update only the fields specific to this scanning type
  fieldMapping.fields.forEach((fieldPath) => {
    const incomingValue = get(incoming, fieldPath);
    // Only update if the incoming value is not null/undefined
    if (incomingValue !== null && incomingValue !== undefined) {
      set(merged, fieldPath, incomingValue);
    }
  });

  // Always update common fields regardless of scanning type
  COMMON_FIELDS.forEach((field) => {
    const incomingValue = get(incoming, field);
    if (incomingValue !== null && incomingValue !== undefined) {
      set(merged, field, incomingValue);
    }
  });

  return merged;
};

/**
 * Helper to check if any scanning is in progress
 */
export const isAnyScanningInProgress = (
  analysis: DocumentAnalysis | undefined
): boolean => {
  if (!analysis) return false;

  return (
    analysis.expenseStatus === DocumentAnalysisStatus.IN_PROGRESS ||
    analysis.queryStatus === DocumentAnalysisStatus.IN_PROGRESS
  );
};

/**
 * Helper to check if all scanning is complete
 */
export const isAllScanningComplete = (
  analysis: DocumentAnalysis | undefined
): boolean => {
  if (!analysis) return true;

  const expenseComplete =
    !analysis.expenseStatus ||
    analysis.expenseStatus === DocumentAnalysisStatus.SCANNED ||
    analysis.expenseStatus === DocumentAnalysisStatus.COMPLETED;

  const queryComplete =
    !analysis.queryStatus ||
    analysis.queryStatus === DocumentAnalysisStatus.SCANNED ||
    analysis.queryStatus === DocumentAnalysisStatus.COMPLETED;

  return expenseComplete && queryComplete;
};
